import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Card, 
  Text, 
  Title, 
  Badge, 
  Button, 
  Progress, 
  Avatar, 
  Group, 
  Stack, 
  Paper,
  RingProgress,
  ThemeIcon,
  Transition,
  Modal,
  Center,
  Box,
  ActionIcon,
  Tooltip,
  Timeline,
  Alert,
  Tabs,
  ScrollArea,
  Indicator,
  NumberInput,
  Select,
} from '@mantine/core';
import { 
  IconPhone, 
  IconMail, 
  IconTrophy, 
  IconFlame, 
  IconStar, 
  IconChevronRight,
  IconBolt,
  IconTarget,
  IconTrendingUp,
  IconClock,
  IconCheck,
  IconAlertCircle,
  IconUser,
  IconBriefcase,
  IconCalendar,
  IconMessage,
  IconChartBar,
  IconRocket,
  IconConfetti,
  IconSettings,
  IconSearch,
  IconPackage,
  IconTrendingDown,
  IconActivity
} from '@tabler/icons-react';
import confetti from 'canvas-confetti';

// Mock data
const salesTeam = [
  { id: 1, name: 'Sarah Chen', avatar: '👩‍💼', points: 2850, streak: 7, level: 'Diamond' },
  { id: 2, name: 'Mike Johnson', avatar: '👨‍💼', points: 2650, streak: 5, level: 'Platinum' },
  { id: 3, name: 'Emily Davis', avatar: '👩‍💻', points: 2400, streak: 3, level: 'Platinum' },
  { id: 4, name: 'You', avatar: '🎯', points: 2200, streak: 4, level: 'Gold', isCurrentUser: true },
  { id: 5, name: 'Alex Rivera', avatar: '👨‍💻', points: 1900, streak: 2, level: 'Gold' }
];

const tasks = [
  { 
    id: 1, 
    customer: 'Boeing Defense', 
    type: 'call', 
    priority: 'critical',
    message: 'Customer needs callback - $2.5M opportunity',
    lastContact: '3 days ago',
    value: '$2.5M',
    health: 'About to drop in health',
    fleet: ['737NG', '787'],
    matchedParts: 12,
    category: 'customer'
  },
  {
    id: 2,
    customer: 'Southwest Airlines',
    type: 'email',
    priority: 'high',
    message: 'Inbound RFQ - Urgent response needed',
    lastContact: '1 hour ago',
    value: '$850K',
    health: 'Hot',
    fleet: ['737-800', '737 MAX'],
    matchedParts: 8,
    category: 'customer'
  },
  {
    id: 3,
    customer: 'Delta TechOps',
    type: 'follow-up',
    priority: 'medium',
    message: 'Quote follow-up required',
    lastContact: '5 days ago',
    value: '$450K',
    health: 'Tempo',
    fleet: ['A320', 'A350'],
    matchedParts: 15,
    category: 'customer'
  },
  {
    id: 4,
    customer: 'APU Control Unit - 737NG',
    type: 'parts',
    priority: 'high',
    message: 'High-margin part - Multiple buyers interested',
    lastContact: 'N/A',
    value: '$125K',
    health: 'Hot opportunity',
    partNumber: 'APU-7375-CU',
    inventory: 3,
    category: 'parts'
  },
  {
    id: 5,
    customer: 'Landing Gear Assembly - A320',
    type: 'parts',
    priority: 'medium',
    message: 'Surplus inventory - Need to move',
    lastContact: 'N/A',
    value: '$89K',
    health: 'Good demand',
    partNumber: 'LG-A320-001',
    inventory: 1,
    category: 'parts'
  }
];

const motivationalPhrases = [
  "🚀 You're on fire today! Keep crushing those goals!",
  "💪 Champions are made one call at a time!",
  "⭐ Excellence is a habit, not an act!",
  "🎯 Focus on progress, not perfection!",
  "🏆 Today's efforts are tomorrow's victories!"
];

const AcceleratorDashboard = () => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [showFireworks, setShowFireworks] = useState(false);
  const [userPoints, setUserPoints] = useState(2200);
  const [completedTasks, setCompletedTasks] = useState(3);
  const [motivationalPhrase, setMotivationalPhrase] = useState(motivationalPhrases[0]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const interval = setInterval(() => {
      setMotivationalPhrase(motivationalPhrases[Math.floor(Math.random() * motivationalPhrases.length)]);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleTaskComplete = (task) => {
    // Trigger fireworks
    setShowFireworks(true);
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      confetti(Object.assign({}, defaults, { 
        particleCount, 
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } 
      }));
      confetti(Object.assign({}, defaults, { 
        particleCount, 
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } 
      }));
    }, 250);

    // Update points with animation
    setUserPoints(prev => prev + 50);
    setCompletedTasks(prev => prev + 1);
    
    setTimeout(() => {
      setShowFireworks(false);
      setSelectedTask(null);
    }, 3000);
  };

  const handleInventoryMatcher = () => {
    // In a real app, this would navigate to the route
    window.location.href = '/sales/inventory-matcher';
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'yellow';
      default: return 'blue';
    }
  };

  const getHealthColor = (health) => {
    if (health === 'Hot' || health === 'Hot opportunity') return 'red';
    if (health === 'Tempo' || health === 'Good demand') return 'yellow';
    if (health === 'About to drop in health') return 'orange';
    return 'blue';
  };

  return (
    <Container size="xl" p="md">
      {/* Header with Leaderboard and Motivation */}
      <Grid mb="xl">
        <Grid.Col span={8}>
          <Card shadow="sm" radius="md" p="lg" withBorder>
            <Title order={3} mb="md">
              <Group>
                <IconTrophy size={24} />
                Sales Leaderboard - Sprint Week 4
              </Group>
            </Title>
            <Group spacing="xs">
              {salesTeam.map((member, index) => (
                <Tooltip key={member.id} label={`${member.points} points | ${member.streak} day streak`}>
                  <Paper
                    p="sm"
                    radius="md"
                    withBorder
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      transform: index === 0 ? 'scale(1.05)' : 'scale(1)',
                      backgroundColor: member.isCurrentUser ? 'var(--mantine-color-blue-light)' : undefined
                    }}
                  >
                    <Stack align="center" spacing={5}>
                      <Group spacing={5}>
                        {index === 0 && <IconTrophy size={16} color="gold" />}
                        <Text size="xl">{member.avatar}</Text>
                      </Group>
                      <Badge color={index === 0 ? 'yellow' : 'blue'} variant="filled" size="sm">
                        #{index + 1}
                      </Badge>
                      <Text size="xs" weight={600}>{member.name}</Text>
                      <Group spacing={3}>
                        <IconFlame size={14} color="orange" />
                        <Text size="xs" c="orange">{member.streak}</Text>
                      </Group>
                    </Stack>
                  </Paper>
                </Tooltip>
              ))}
            </Group>
          </Card>
        </Grid.Col>
        
        <Grid.Col span={4}>
          <Card shadow="sm" radius="md" p="lg" withBorder style={{ height: '100%' }}>
            <Stack align="center" justify="center" style={{ height: '100%' }}>
              <Transition mounted={true} transition="slide-down" duration={500}>
                {(styles) => (
                  <Text size="lg" weight={700} align="center" style={styles}>
                    {motivationalPhrase}
                  </Text>
                )}
              </Transition>
              <Group>
                <RingProgress
                  size={80}
                  thickness={8}
                  sections={[{ value: (completedTasks / 10) * 100, color: 'lime' }]}
                  label={
                    <Center>
                      <ThemeIcon color="lime" variant="light" radius="xl" size="lg">
                        <IconBolt size={20} />
                      </ThemeIcon>
                    </Center>
                  }
                />
                <Stack spacing={0}>
                  <Text size="sm">Daily Goal</Text>
                  <Text size="xl" weight={700}>{completedTasks}/10</Text>
                </Stack>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Task Completed Counter */}
      <Card shadow="sm" radius="md" p="md" withBorder mb="md">
        <Group position="apart">
          <Group spacing="md">
            <ThemeIcon size="lg" radius="xl" color="green" variant="light">
              <IconCheck />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Tasks Completed Today</Text>
              <Text size="xl" weight={700}>{completedTasks}</Text>
            </div>
          </Group>
          <Badge size="lg" color="green" variant="light">
            +{completedTasks * 50} points earned
          </Badge>
        </Group>
      </Card>

      {/* Main Content Area */}
      <Grid>
        {/* Task Queue */}
        <Grid.Col span={7}>
          <Card shadow="sm" radius="md" p="lg" withBorder>
            <Group position="apart" mb="md">
              <Title order={3}>
                <Group spacing="sm">
                  <IconPhone size={24} />
                  Priority Queue
                  <Indicator color="red" processing>
                    <Badge color="red" variant="filled">{tasks.length} Active</Badge>
                  </Indicator>
                </Group>
              </Title>
            </Group>
            
            <ScrollArea style={{ height: 500 }}>
              <Stack spacing="md">
                {tasks.map((task, index) => (
                  <Paper
                    key={task.id}
                    p="md"
                    radius="md"
                    withBorder
                    style={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateX(5px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                    onClick={() => setSelectedTask(task)}
                  >
                    <Group position="apart">
                      <Group>
                        <ThemeIcon 
                          size="lg" 
                          radius="xl" 
                          color={task.category === 'parts' ? 'violet' : getPriorityColor(task.priority)}
                          variant="light"
                        >
                          {task.category === 'parts' ? <IconPackage /> :
                           task.type === 'call' ? <IconPhone /> : 
                           task.type === 'email' ? <IconMail /> : 
                           <IconClock />}
                        </ThemeIcon>
                        <Stack spacing={0}>
                          <Group spacing="xs">
                            <Text size="md" weight={600}>{task.customer}</Text>
                            {task.category === 'customer' && (
                              <Badge size="sm" color={getPriorityColor(task.priority)}>
                                {task.priority}
                              </Badge>
                            )}
                            {task.category === 'parts' && (
                              <Badge size="sm" color="violet">
                                PARTS
                              </Badge>
                            )}
                            <Badge size="sm" color="cyan" variant="dot">
                              {task.value}
                            </Badge>
                          </Group>
                          <Text size="sm" c="dimmed">{task.message}</Text>
                          <Group spacing="xs" mt={5}>
                            {task.category === 'customer' && (
                              <>
                                <Badge size="xs" color="gray" leftSection={<IconClock size={10} />}>
                                  {task.lastContact}
                                </Badge>
                                <Badge size="xs" color={getHealthColor(task.health)} variant="light">
                                  {task.health}
                                </Badge>
                                <Badge size="xs" color="blue" variant="light">
                                  {task.matchedParts} parts match
                                </Badge>
                              </>
                            )}
                            {task.category === 'parts' && (
                              <>
                                <Badge size="xs" color="gray" variant="light">
                                  P/N: {task.partNumber}
                                </Badge>
                                <Badge size="xs" color="green" variant="light">
                                  {task.inventory} in stock
                                </Badge>
                                <Badge size="xs" color={getHealthColor(task.health)} variant="light">
                                  {task.health}
                                </Badge>
                              </>
                            )}
                          </Group>
                        </Stack>
                      </Group>
                      <ActionIcon 
                        size="lg" 
                        radius="xl" 
                        variant="light" 
                        color="blue"
                      >
                        <IconChevronRight />
                      </ActionIcon>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </ScrollArea>
          </Card>
        </Grid.Col>

        {/* Customer Intelligence Panel */}
        <Grid.Col span={5}>
          <Card shadow="sm" radius="md" p="lg" withBorder style={{ height: '100%' }}>
            {selectedTask ? (
              <Stack>
                <Group position="apart">
                  <Title order={4}>
                    <Group>
                      {selectedTask.category === 'parts' ? <IconPackage /> : <IconBriefcase />}
                      {selectedTask.customer}
                    </Group>
                  </Title>
                  <Badge size="lg" color={getHealthColor(selectedTask.health)}>
                    {selectedTask.health}
                  </Badge>
                </Group>
                
                <Paper p="md" radius="md" withBorder>
                  <Group position="apart" mb="xs">
                    <Text size="sm" c="dimmed">
                      {selectedTask.category === 'parts' ? 'Part Value' : 'Opportunity Value'}
                    </Text>
                    <Text size="lg" weight={700} c="cyan">{selectedTask.value}</Text>
                  </Group>
                  <Progress value={75} color="cyan" size="sm" animate />
                </Paper>

                {selectedTask.category === 'customer' && selectedTask.fleet && (
                  <Paper p="md" radius="md" withBorder>
                    <Text size="sm" weight={600} mb="xs">Fleet Composition</Text>
                    <Group spacing="xs">
                      {selectedTask.fleet.map(aircraft => (
                        <Badge key={aircraft} color="blue" variant="filled">{aircraft}</Badge>
                      ))}
                    </Group>
                  </Paper>
                )}

                {selectedTask.category === 'parts' && (
                  <Paper p="md" radius="md" withBorder>
                    <Text size="sm" weight={600} mb="xs">Part Details</Text>
                    <Stack spacing="xs">
                      <Group position="apart">
                        <Text size="xs" c="dimmed">Part Number:</Text>
                        <Text size="sm" weight={600}>{selectedTask.partNumber}</Text>
                      </Group>
                      <Group position="apart">
                        <Text size="xs" c="dimmed">Inventory:</Text>
                        <Text size="sm" weight={600}>{selectedTask.inventory} units</Text>
                      </Group>
                    </Stack>
                  </Paper>
                )}

                <Paper p="md" radius="md" withBorder>
                  <Text size="sm" weight={600} mb="md">Actions</Text>
                  <Button 
                    variant="light" 
                    color="blue" 
                    leftIcon={<IconSearch />}
                    fullWidth
                    onClick={handleInventoryMatcher}
                  >
                    Inventory Matcher
                  </Button>
                </Paper>

                <Group grow>
                  <Button 
                    size="lg" 
                    color="green" 
                    leftIcon={<IconPhone />}
                    onClick={() => handleTaskComplete(selectedTask)}
                  >
                    Complete Call
                  </Button>
                  <Button 
                    size="lg" 
                    color="blue" 
                    leftIcon={<IconMail />}
                    variant="light"
                  >
                    Send Email
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Center style={{ height: '100%' }}>
                <Stack align="center" spacing="md">
                  <IconTarget size={64} color="var(--mantine-color-blue-6)" />
                  <Text size="lg" c="dimmed" align="center">
                    Select a task from the queue to view details
                  </Text>
                </Stack>
              </Center>
            )}
          </Card>
        </Grid.Col>
      </Grid>

      {/* KPI Dashboard Placeholder */}
      <Card shadow="sm" radius="md" p="lg" mt="xl" withBorder>
        <Title order={4} mb="md">Analytics Dashboard</Title>
        <Text c="dimmed" size="sm" mb="md">
          This section is a placeholder - analytics requirements are still being defined.
        </Text>
        <Tabs value={activeTab} onTabChange={setActiveTab} variant="pills">
          <Tabs.List>
            <Tabs.Tab value="overview" icon={<IconChartBar size={16} />}>Overview</Tabs.Tab>
            <Tabs.Tab value="performance" icon={<IconTrendingUp size={16} />}>Performance</Tabs.Tab>
            <Tabs.Tab value="activity" icon={<IconBolt size={16} />}>Activity</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <Grid>
              <Grid.Col span={3}>
                <Paper p="md" radius="md" withBorder>
                  <Group position="apart">
                    <Stack spacing={0}>
                      <Text size="xs" c="dimmed">Your Points</Text>
                      <Text size="xl" weight={700}>{userPoints}</Text>
                      <Text size="xs" c="green">+50 today</Text>
                    </Stack>
                    <RingProgress
                      size={60}
                      thickness={6}
                      sections={[{ value: 73, color: 'blue' }]}
                      label={
                        <Center>
                          <IconStar size={20} />
                        </Center>
                      }
                    />
                  </Group>
                </Paper>
              </Grid.Col>
              
              <Grid.Col span={3}>
                <Paper p="md" radius="md" withBorder>
                  <Group position="apart">
                    <Stack spacing={0}>
                      <Text size="xs" c="dimmed">Tasks Today</Text>
                      <Text size="xl" weight={700}>{completedTasks}/10</Text>
                      <Text size="xs" c="green">On track! 🔥</Text>
                    </Stack>
                    <ThemeIcon size="xl" radius="xl" color="green" variant="light">
                      <IconCheck />
                    </ThemeIcon>
                  </Group>
                </Paper>
              </Grid.Col>

              <Grid.Col span={3}>
                <Paper p="md" radius="md" withBorder>
                  <Group position="apart">
                    <Stack spacing={0}>
                      <Text size="xs" c="dimmed">Response Time</Text>
                      <Text size="xl" weight={700}>12 min</Text>
                      <Text size="xs" c="green">-3 min avg</Text>
                    </Stack>
                    <ThemeIcon size="xl" radius="xl" color="blue" variant="light">
                      <IconBolt />
                    </ThemeIcon>
                  </Group>
                </Paper>
              </Grid.Col>

              <Grid.Col span={3}>
                <Paper p="md" radius="md" withBorder>
                  <Group position="apart">
                    <Stack spacing={0}>
                      <Text size="xs" c="dimmed">Win Rate</Text>
                      <Text size="xl" weight={700}>68%</Text>
                      <Text size="xs" c="blue">Top 20%</Text>
                    </Stack>
                    <ThemeIcon size="xl" radius="xl" color="yellow" variant="light">
                      <IconTrophy />
                    </ThemeIcon>
                  </Group>
                </Paper>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="performance" pt="md">
            <Center py="xl">
              <Text c="dimmed">Performance metrics coming soon...</Text>
            </Center>
          </Tabs.Panel>

          <Tabs.Panel value="activity" pt="md">
            <Center py="xl">
              <Text c="dimmed">Activity tracking coming soon...</Text>
            </Center>
          </Tabs.Panel>
        </Tabs>
      </Card>

      {/* Fireworks Modal */}
      <Modal
        opened={showFireworks}
        onClose={() => setShowFireworks(false)}
        size="sm"
        centered
        withCloseButton={false}
        styles={{
          modal: {
            background: 'transparent',
            boxShadow: 'none'
          }
        }}
      >
        <Center>
          <Stack align="center">
            <Text size="xl" weight={700} style={{ textShadow: '0 0 20px rgba(0,0,0,0.8)' }}>
              🎉 AWESOME WORK! 🎉
            </Text>
            <Text size="lg">+50 Points Earned!</Text>
          </Stack>
        </Center>
      </Modal>
    </Container>
  );
};

export default AcceleratorDashboard;